import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import Sidebar from '../components/shared/Sidebar';
import Header from '../components/shared/Header';
import MobileTabBar from '../components/shared/MobileTabBar';
import OverviewPage from '../components/pages/OverviewPage';
import CampaignsPage from '../components/pages/CampaignsPage';
import LocationsPage from '../components/pages/LocationsPage';
import InsightsPage from '../components/pages/InsightsPage';

export default function DashboardLayout() {
  const { brand, dataSources, fetcher } = useClient();
  const features = dataSources.features || {};

  const [activePage, setActivePage] = useState('overview');
  const [dateRange, setDateRange] = useState('mtd');
  const [summary, setSummary] = useState(null);
  const [locations, setLocations] = useState(null);
  const [brandCampaign, setBrandCampaign] = useState(null);
  const [campaignCards, setCampaignCards] = useState(null);
  const [dailyTrend, setDailyTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = (range) => {
    return Promise.all([
      fetcher.fetchSummary(range),
      fetcher.fetchLocations(range),
      fetcher.fetchDailyTrend(range)
    ]).then(([summaryData, locationData, trendData]) => {
        setSummary(summaryData);
        setLocations(locationData.locations || []);
        setBrandCampaign(locationData.brand || null);
        setCampaignCards(locationData.campaignCards || null);
        setDailyTrend(trendData);
      });
  };

  useEffect(() => {
    loadData(dateRange).finally(() => setLoading(false));
  }, []);

  // Re-fetch when date range changes
  useEffect(() => {
    if (!loading) {
      setRefreshing(true);
      loadData(dateRange).finally(() => {
        setRefreshing(false);
        setRefreshKey(k => k + 1);
      });
    }
  }, [dateRange]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData(dateRange).finally(() => {
      setRefreshing(false);
      setRefreshKey(k => k + 1);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3"
            style={{ borderColor: brand.colors?.primary || '#2563eb' }}
          />
          <p className="text-gray-400 text-sm">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (activePage) {
      case 'campaigns':
        return (
          <CampaignsPage
            campaignCards={campaignCards}
            brandCampaign={brandCampaign}
            refreshKey={refreshKey}
            dateRange={dateRange}
          />
        );
      case 'insights':
        return <InsightsPage refreshKey={refreshKey} dateRange={dateRange} />;
      case 'locations':
        return features.locations ? <LocationsPage locations={locations} /> : null;
      case 'overview':
      default:
        return (
          <OverviewPage
            summary={summary}
            dailyTrend={dailyTrend}
            refreshKey={refreshKey}
            dateRange={dateRange}
          />
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activePage={activePage} onNavigate={setActivePage} />

      <div className="flex-1 flex flex-col min-h-screen">
        <Header
          onRefresh={handleRefresh}
          refreshing={refreshing}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />

        <main className="flex-1 p-6 pb-24 md:pb-6 overflow-y-auto">
          <div className="max-w-5xl mx-auto page-transition">
            {renderPage()}
          </div>

          {/* Contact CTA */}
          <div className="max-w-5xl mx-auto mt-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
              <svg className="w-8 h-8 mx-auto mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-700 mb-1"
                  style={{ fontFamily: brand.typography?.headerFont }}>
                Have a question about your account?
              </h3>
              <p className="text-xs text-gray-400 mb-4">Ask us a question or let's set up a meeting</p>
              <a
                href={`mailto:chris@beechppc.com?subject=${encodeURIComponent(`A message from our client portal - ${brand.brandName}`)}`}
                className="inline-block px-6 py-2.5 rounded-lg text-white font-medium text-sm transition-colors hover:opacity-90"
                style={{ backgroundColor: brand.colors?.primary || '#2563eb' }}
              >
                Speak to us today
              </a>
            </div>
          </div>
        </main>
      </div>

      <MobileTabBar activePage={activePage} onNavigate={setActivePage} />
    </div>
  );
}
